import { Controller } from "@hotwired/stimulus"
import { rollSwap } from "../helpers/readout_roll"

// Dashboard band — This month and Plan.
//
// The band opens on This month (Left · Spent · Upcoming). A tap on the number,
// or on the eyebrow above it, rolls all three groups over to Plan (Budgeted ·
// Flexible · Income) and back. Same roll as the budget card's end column
// (readout_roll): the old figures lift out, the new ones rise into the boxes
// they left, and the arrow after the eyebrow turns over.
//
// The view is remembered on the device, so a user who leaves the band on Plan
// opens the app on Plan. Restoring it is instant: no roll on load.
const STORAGE_KEY = "floosyo:band-view"
const DEFAULT_VIEW = "month"

export default class extends Controller {
  static targets = [
    "label", "value", "glyph", "number",
    "startLabel", "startValue", "startNumber",
    "endLabel", "endValue", "endNumber"
  ]
  static values = {
    views: Object,   // { month: { start: {label, amount, tone}, center: {...}, end: {...} }, plan: {...} }
    selected: String
  }

  static KEYS = ["month", "plan"]

  connect() {
    const stored = this._stored()
    if (stored !== this.selectedValue && this.viewsValue[stored]) this._show(stored)
  }

  // data-action="click->flexible-toggle#toggle"
  toggle(event) {
    const [a, b] = this.constructor.KEYS
    const key = this.selectedValue === a ? b : a

    this._tap(event.currentTarget)
    this._store(key)

    this._roll?.cancel()
    this._roll = rollSwap(this._rolling(), () => this._apply(key))
    this._mark(key)
  }

  disconnect() {
    this._roll?.cancel()
  }

  _show(key) {
    this._apply(key)
    this._mark(key)
  }

  _mark(key) {
    this.selectedValue = key
    this.element.dataset.view = key
    if (this.hasGlyphTarget) this.glyphTarget.classList.toggle("is-flipped", key !== DEFAULT_VIEW)
  }

  _rolling() {
    return [
      this.labelTarget, this.valueTarget,
      this.startLabelTarget, this.startValueTarget,
      this.endLabelTarget, this.endValueTarget
    ]
  }

  // Amounts come from the server already formatted. The end figures carry the
  // money-sym span (money_html), so they are written as HTML; the centre
  // figure is plain text, as it has always been.
  _apply(key) {
    const view = this.viewsValue[key]
    if (!view) return

    this.labelTarget.textContent = view.center.label
    this.valueTarget.textContent = view.center.amount
    this.startLabelTarget.textContent = view.start.label
    this.startValueTarget.innerHTML = view.start.amount
    this.endLabelTarget.textContent = view.end.label
    this.endValueTarget.innerHTML = view.end.amount

    this._tone(this.numberTarget, "center", key)
    this._tone(this.startNumberTarget, "start", key)
    this._tone(this.endNumberTarget, "end", key)
  }

  _tone(element, slot, key) {
    Object.values(this.viewsValue).forEach((view) => {
      if (view[slot].tone) element.classList.remove(view[slot].tone)
    })
    const tone = this.viewsValue[key][slot].tone
    if (tone) element.classList.add(tone)
  }

  _tap(element) {
    const haptic = this.application.getControllerForElementAndIdentifier(element, "bridge--haptic")
    haptic?.tap()
  }

  _stored() {
    try {
      return localStorage.getItem(STORAGE_KEY) || DEFAULT_VIEW
    } catch (_) {
      return DEFAULT_VIEW
    }
  }

  _store(key) {
    try {
      localStorage.setItem(STORAGE_KEY, key)
    } catch (_) {
      // Private mode, quota — the band still flips, it just forgets.
    }
  }
}
